import { Box, Collapse, Fade, InputAdornment, Modal, Stack, TextField, Typography } from '@mui/material'
import { observer, useLocalObservable } from 'mobx-react-lite'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { T } from 'GlobalStores/Cores/Translator/useTranslate'
import PasswordStrengthBar from '../Settings/PasswordStrengthBar'
import useComplexPassword from 'Helpers/useComplexPassword'
import { DefaultButton } from '../Forms'
import StoreAuth from 'GlobalStores/User/StoreAuth'
import userAPI from 'GlobalStores/User/APIs'
import { ReactComponent as CloseEyeIcon } from '../Forms/Assets/password-off-2.svg'
import { ReactComponent as OpenEyeIcon } from '../Forms/Assets/password-on-2.svg'

const useWindowSize = () => {
    const [width, setWidth] = useState(window.innerWidth)
    const [height, setHeight] = useState(window.innerHeight)
    useEffect(() => {
        const handleResize = () => {
            setWidth(window.innerWidth)
            setHeight(window.innerHeight)
        }
        window.addEventListener('resize', handleResize)
    }, [])

    return [width, height]
}

const EnforceChangePasswordModal = observer(({
    isShowModal,
    baId,
    onChangePassword,
    setIsShowChangePasswordSuccessModal
}) => {

    const DESCRIPTIONS = useMemo(() => {
        return [
            'Be_minimun_8_characters',
            'include_least_one_lowercase_letter',
            'include_least_one_uppercase_letter',
            'include_least_one_number',
            'include_least_one_special_character',
        ]
    }, [])

    const localStore = useLocalObservable(() => ({
        loginPassword: '',
        password: '',
        errorMessages: [],
        isHidePassword: true,
        isButtonLoading: false,
        isShowModal: false,
        isShowSuccessModal: true,
    }))


    // const [windowWidth] = useWindowSize()

    const { errors: errorValidation } = useComplexPassword(localStore.password)

    const handleClearErrorMessages = useCallback(() => {
        if (localStore.errorMessages.length > 0) {
            localStore.errorMessages = []
        }
    }, [])

    const handleToggleHidePassword = useCallback(() => {
        localStore.isHidePassword = !localStore.isHidePassword
    }, [localStore.isHidePassword])

    const handleChangePassword = useCallback((newPassword) => {
        handleClearErrorMessages()
        localStore.password = newPassword
    }, [localStore.password])

    const handleStopButtonLoading = useCallback(async () => {
        await new Promise(resolve => setTimeout(resolve, 500))

        localStore.isButtonLoading = false
    }, [localStore.isButtonLoading])

    const handleBeforeLoginWithNewPassword = useCallback(async () => {
        return new Promise((resolve) => {
            StoreAuth.setIsEnforceChangePassword(false)

            onChangePassword(localStore.password)

            resolve()
        })

    }, [localStore.password])

    const handleChangePasswordProcess = useCallback(async () => {
        const token = sessionStorage.getItem('hydra_token_enforce_change_password')

        await userAPI.changePassword(token, localStore.password)

        sessionStorage.removeItem('hydra_token_enforce_change_password')

    }, [baId, localStore.password])

    const handleSubmit = useCallback(async () => {
        localStore.isButtonLoading = true


        if (errorValidation.length > 0) {
            localStore.errorMessages = errorValidation
            await handleStopButtonLoading()
            return

        } else {
            try {

                await handleChangePasswordProcess()

                await handleBeforeLoginWithNewPassword()

                await handleStopButtonLoading()

                localStore.isShowModal = false

                // Open Success Modal
                setIsShowChangePasswordSuccessModal(true)
            } catch (error) {
                console.log('EnforceChangePasswordModal handleSubmit error', error)
                localStore.errorMessages = [error.response?.data?.error?.error_message || error.response?.data?.error?.message || 'An error occurred']
                await handleStopButtonLoading()
            }
            return
        }

    }, [localStore.password, errorValidation])

    useEffect(() => {
        localStore.isShowModal = isShowModal
    }, [isShowModal])


    return (

        <Modal
            open={localStore.isShowModal}
            keepMounted={true}
            sx={{
                '*': {
                    fontFamily: 'Inter, NotoSansThai, sans-serif!important',
                    boxSizing: 'border-box!important',
                }
            }}
        >
            <Fade
                in={localStore.isShowModal}
                timeout={800}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'absolute',
                        left: '50%',
                        top: {
                            xs: '50%',
                            "@media (min-width: 768px)": {
                                top: '40%'
                            }
                        },

                        transform: 'translate(-50%, -50%)',
                        maxHeight: {
                            xs: 'calc(100vh - 32px)',
                            md: 'calc(100vh - 64px)'
                        },
                        overflowY: 'auto',
                        minWidth: '343px',
                        width: 'calc(100% - 32px)',
                        maxWidth: '920px',
                        backgroundColor: '#fff',
                        borderRadius: '12px',
                        padding: {
                            xs: '24px',
                            lg: '32px'
                        },
                        ':focus-visible': {
                            outline: 'none'
                        }
                    }}
                >
                    <Stack
                        sx={{
                            mb: "8px",
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: '18px',
                                fontWeight: 600,
                                color: '#003B6F',
                                lineHeight: '25px',
                                fontFamily: 'Poppins, NotoSansThai, sans-serif!important',
                            }}
                        >
                            <T>password_update_required</T>
                        </Typography>
                    </Stack>

                    <Stack
                        sx={{
                            mb: {
                                xs: '24px',
                                lg: '30px'
                            }
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: {
                                    xs: '11px',
                                    md: '12px'
                                },
                                fontWeight: 400,
                                color: '#003B6F',
                                lineHeight: '16px',
                            }}
                        >
                            <T>enforce_change_password_description</T>
                        </Typography>
                    </Stack>

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            "@media (min-width: 768px)": {
                                flexDirection: 'row-reverse',
                                mb: '32px'
                            },
                            width: '100%',
                            gap: "24px",
                        }}
                    >
                        <Stack
                            sx={{
                                flex: 1,
                            }}
                        >
                            <Box
                                sx={{
                                    backgroundColor: '#FFF1DC',
                                    padding: '16px',
                                    "@media (min-width: 768px)": {
                                        padding: '24px'
                                    },
                                    borderRadius: '8px',
                                    width: '100%',
                                }}>
                                <Typography
                                    sx={{
                                        fontSize: {
                                            xs: '14px',
                                            "@media (min-width: 768px)": {
                                                fontSize: '16px'
                                            }
                                        },
                                        fontWeight: 700,
                                        color: '#003B6F',
                                        marginBottom: {
                                            xs: '8px',
                                            "@media (min-width: 768px)": {
                                                marginBottom: '16px'
                                            }
                                        }
                                    }}
                                >
                                    <T>your_password_must_be_meet_criteria</T>
                                </Typography>

                                {DESCRIPTIONS.map((item, index) => (
                                    <Stack
                                        key={index}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            gap: '5px',
                                        }}
                                    >
                                        <ul style={{
                                            listStyleType: 'disc',
                                            margin: '0',
                                            paddingLeft: '14px'
                                        }}>
                                            <Typography
                                                component="li"
                                                sx={{
                                                    fontSize: {
                                                        xs: '10px',
                                                        "@media (min-width: 768px)": {
                                                            fontSize: '12px'
                                                        }
                                                    },
                                                    fontWeight: 400,
                                                    color: '#003B6F',
                                                    lineHeight: '15px',
                                                    letterSpacing: 'normal'

                                                }}>

                                                <T>{item}</T>
                                            </Typography>
                                        </ul>
                                    </Stack>
                                ))}
                            </Box>
                        </Stack>

                        <Stack
                            sx={{
                                flex: 1,
                                justifyContent: 'center'
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    width: '100%'

                                }}
                            >
                                <Typography variant="desc"
                                    sx={{
                                        fontSize: '14px',
                                        fontWeight: '400',
                                        color: '#9EA9B9',
                                        mb: '10px'
                                    }}>
                                    <T>new_password</T>
                                </Typography>
                                <TextField
                                    onChange={(e) => handleChangePassword(e.target.value)}
                                    type={localStore.isHidePassword ? 'password' : 'text'}
                                    sx={textFieldStyle}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                {localStore.isHidePassword ? (
                                                    <CloseEyeIcon
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={handleToggleHidePassword}
                                                    />

                                                ) : (
                                                    <OpenEyeIcon
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={handleToggleHidePassword}
                                                    />
                                                )}
                                            </InputAdornment>
                                        ),
                                        'aria-autocomplete': 'none',
                                        spellCheck: 'false',
                                        autoComplete: 'off'
                                    }}
                                    disabled={localStore.isButtonLoading}
                                />

                                <Box sx={{ mt: '12px' }}>

                                    <PasswordStrengthBar
                                        password={localStore.password}
                                        isShowDescription={false}
                                    />
                                </Box>

                                <ErrorMessagesBox errorMessages={localStore.errorMessages} />

                                <Stack mt='24px'>
                                    <DefaultButton
                                        disabled={localStore.isButtonLoading || localStore.errorMessages.length > 0 || localStore.password === ''}
                                        type="submit"
                                        onClick={handleSubmit}
                                        titleText={<T>btn_update</T>}
                                        isloading={String(localStore.isButtonLoading)}
                                        sx={{
                                            fontFamily: 'Poppins, NotoSansThai, sans-serif!important',
                                            fontSize: '18px',
                                            color: '#FFFFFF',
                                            fontWeight: '600',
                                            height: '40px',
                                            width: '140px',
                                            '&:disabled': {
                                                color: '#FFFFFF',
                                                backgroundColor: '#CCDEEF'
                                            },
                                            backgroundColor: '#5A8FC3',
                                        }}
                                    />
                                </Stack>
                            </Box>
                        </Stack>
                    </Box>
                </Box>
            </Fade>
        </Modal>
    )
})

const textFieldStyle = {
    '& .MuiInputBase-input': {
        textAlign: 'left',
        color: '#003B6F',
        fontSize: '14px',
        padding: '20px 16px',
        height: '58px',
    },
    '& .MuiOutlinedInput-root': {
        borderRadius: '8px',
        'fieldset': {
            border: '1px solid #ECF0F4',
        },
        '&:hover fieldset': {
            border: '1px solid #ECF0F4',
        },
        '&.Mui-focused fieldset': {
            border: '1px solid #ECF0F4',
        },

    }
}

export default EnforceChangePasswordModal


const ErrorMessages = ({ errorMessages }) => {

    return errorMessages.length > 0 && (
        errorMessages.map((item, index, array) => (
            <Stack
                key={index}
                sx={{
                    marginTop: index > 0 && '10px',
                    paddingBottom: index < array.length - 1 && '10px',
                    borderBottom: index < errorMessages.length - 1 && '1px dashed #FF6187',
                }}
            >
                <Typography
                    variant="desc"
                    sx={{ width: '100%' }}
                    fontSize="14px"
                    fontWeight={400}
                    color="#FE5D7C"
                    lineHeight={'20px'}
                >
                    <T>{item}</T>
                </Typography>
            </Stack>
        ))
    )

}

const ErrorMessagesBox = ({ errorMessages }) => {
    const errorComplexPasswordRef = useRef(null) // If appear error, scroll to error

    useEffect(() => {
        const timer = setTimeout(() => {
            if (errorMessages.length > 0 && errorComplexPasswordRef.current) {
                errorComplexPasswordRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });
            }
        }, 100);

        return () => clearTimeout(timer);
    }, [errorMessages])

    const [, windowHeight] = useWindowSize()


    if (windowHeight >= 700) {
        return (

            <Collapse in={errorMessages.length > 0} timeout={500}>
                <Box
                    sx={{
                        maxWidth: '100%',
                        borderRadius: '8px',
                        backgroundColor: '#FFE7EB',
                        padding: '15px 27px',
                        textAlign: 'center',
                        mt: '24px',
                    }}
                >
                    <ErrorMessages errorMessages={errorMessages} />
                </Box>
            </Collapse>

        )
    } else {
        return errorMessages.length > 0 && (
            <Box
                ref={errorComplexPasswordRef}
                sx={{
                    maxWidth: '100%',
                    borderRadius: '8px',
                    backgroundColor: '#FFE7EB',
                    padding: '15px 27px',
                    textAlign: 'center',
                    mt: '24px',
                }}
            >
                <ErrorMessages errorMessages={errorMessages} />
            </Box>
        )
    }
}
